import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    TablePagination,
    TextField,
    Button,
    Snackbar,
    Alert,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Breadcrumbs,
    Link,
} from '@mui/material';
import TableComponent from '../../widgets/TableComponent';
import RequestService from '../../services/RequestService';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';
import RewardBadge from '../../widgets/RewardBadge';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import { ReactComponent as XpIcon } from '../../assets/icons/xp.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import UserContentReviewDialog from '../../widgets/usercontents/UserContentReviewDialog';
import parseSpringDate from '../../utils/parseSpringDate';

const ContentsHistoryPage = () => {
    const { user, hasPermission } = useContext(UserContext);

    const [contents, setContents] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [userId, setuserId] = useState('');
    const [users, setUsers] = useState([]);
    const [groupId, setGroupId] = useState('');
    const [groups, setGroups] = useState([]);
    const [approved, setApproved] = useState('');
    const [userContent, setSelectedUserContent] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchHistory = async () => {
        try {
            const data =
                await RequestService.getReviewedUserContentsWithFilters(
                    user.company.id,
                    {
                        page,
                        size: rowsPerPage,
                        userId,
                        groupId,
                        approved: approved !== '' ? approved : undefined,
                    }
                );

            setContents(data.content || []);
            setTotalElements(data.totalElements || 0);
        } catch (error) {
            console.error('Erro ao carregar histórico de conteúdos:', error);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, [page, rowsPerPage]);

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const userData = await RequestService.getUsersByCompany(
                    user.company.id
                );
                setUsers(Array.isArray(userData) ? userData : []);

                const groupData = await RequestService.getGroups(
                    user.company.id
                );
                setGroups(Array.isArray(groupData) ? groupData : []);
            } catch (error) {
                console.error('Erro ao carregar filtros:', error);
            }
        };

        fetchFilters();
    }, [user.company.id]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRowClick = async (row) => {
        try {
            const data = await RequestService.getUserContentDetails(row.id);
            setSelectedUserContent(data);
            setIsDialogOpen(true);
        } catch (err) {
            console.error('Erro ao buscar detalhes do conteúdo:', err);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleSuccessReview = (text) => {
        setSnackbarMessage(text);
        setSnackbarOpen(true);
    };

    const columns = [
        {
            label: 'Título',
            field: 'title',
            renderCell: (_, row) => row.content?.title || '-',
        },
        {
            label: 'Usuário',
            field: 'user',
            renderCell: (_, row) => row.user?.name || '-',
        },
        {
            label: 'Data',
            field: 'createdAt',
            renderCell: (_, row) => {
                if (!row.createdAt) return '-';
                const date = parseSpringDate(row.createdAt);
                return date.toLocaleString('pt-BR');
            },
        },
        {
            label: 'Status',
            field: 'approved',
            renderCell: (value) => (
                <Typography
                    variant="body2"
                    sx={{ ml: 0.5, color: value ? '#1F83FF' : 'red' }}
                >
                    {value ? 'Aprovado' : 'Rejeitado'}
                </Typography>
            ),
        },
        {
            label: 'Experiência',
            field: 'exp',
            renderCell: (_, row) => (
                <RewardBadge
                    icon={
                        <XpIcon
                            style={{ color: '#134F99', fontSize: '24px' }}
                        />
                    }
                    value={row.content?.exp}
                    positive={row.attitudeType !== 'NEGATIVE'}
                />
            ),
        },
        {
            label: 'Moedas',
            field: 'coin',
            renderCell: (_, row) => (
                <RewardBadge
                    icon={
                        <CoinIcon
                            style={{ color: '#134F99', fontSize: '24px' }}
                        />
                    }
                    value={row.content?.coin}
                    positive={row.attitudeType !== 'NEGATIVE'}
                />
            ),
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row) => (
                <>
                    {hasPermission('CONTENT_APPROVE') && (
                        <Link
                            component="button"
                            underline="none"
                            onClick={() => handleRowClick(row)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'blue',
                            }}
                        >
                            <EditIcon sx={{ color: '#1F83FF' }} />
                            <Typography
                                variant="body2"
                                sx={{ ml: 0.5, color: '#1F83FF' }}
                            >
                                Revisar
                            </Typography>
                        </Link>
                    )}
                </>
            ),
        },
    ];

    const renderMobileLayout = () => {
        if (contents.length === 0) {
            return <Typography>Nenhum conteúdo encontrado.</Typography>;
        }

        return (
            <Box>
                {contents.map((item) => (
                    <Box
                        key={item.id}
                        sx={{
                            border: '1px solid #ddd',
                            borderRadius: 2,
                            padding: 2,
                            marginBottom: 2,
                        }}
                    >
                        <Typography variant="subtitle1">
                            {item.content?.title || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Usuário: {item.user?.name || '-'}
                        </Typography>
                        <Typography variant="body2">
                            Aprovado: {item.approved ? '✅' : '❌'}
                        </Typography>
                        <Typography variant="body2">
                            Experiência: {item.content?.exp || 0}
                        </Typography>
                        <Typography variant="body2">
                            Moedas: {item.content?.coin || 0}
                        </Typography>
                    </Box>
                ))}

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                />
            </Box>
        );
    };

    const renderDesktopLayout = () => (
        <Box>
            <TableComponent columns={columns} rows={contents} />

            <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20, 50]}
            />
        </Box>
    );

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        GameUP
                    </Link>
                    <Link underline="hover" color="inherit">
                        Missões
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Histórico
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Histórico de conclusões
                </Typography>
            </Grid>

            <Box sx={{ my: 2 }}>
                <Box display="flex" flexWrap="wrap" gap={2}>
                    {/* Filtro Usuário */}
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Usuário</InputLabel>
                        <Select
                            value={userId}
                            label="Usuário"
                            onChange={(e) => setuserId(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {users.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    {u.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Grupo</InputLabel>
                        <Select
                            value={groupId}
                            label="Grupo"
                            onChange={(e) => setGroupId(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {groups.map((g) => (
                                <MenuItem key={g.id} value={g.id}>
                                    {g.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Aprovação</InputLabel>
                        <Select
                            value={approved}
                            label="Aprovação"
                            onChange={(e) => setApproved(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value={true}>Aprovado</MenuItem>
                            <MenuItem value={false}>Rejeitado</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Botão de aplicar */}
                    <Button
                        variant="contained"
                        sx={{ height: 45 }}
                        onClick={() => {
                            setPage(0);
                            fetchHistory();
                        }}
                    >
                        Aplicar Filtros
                    </Button>
                </Box>
            </Box>

            {isMobile ? renderMobileLayout() : renderDesktopLayout()}

            <UserContentReviewDialog
                open={isDialogOpen}
                onSucessApprove={(text) => {
                    handleSuccessReview(text);
                    setIsDialogOpen(false);
                    setSelectedUserContent(null);
                    fetchHistory(); // Atualiza os dados
                }}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setIsDialogOpen(false);
                        setSelectedUserContent(null);
                    }
                }}
                content={userContent}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ContentsHistoryPage;
